import _ from 'lodash'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import ChairIcon from '@mui/icons-material/Chair'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded'
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import MessageIcon from '@mui/icons-material/Message'

import { Network } from '@/core/models/network.model'

import { Badge } from '@mui/material'
import { useNetworkMemberPermissions } from './useNetworkMemberPermissions'

export function useNetworkMenuItems(network?: Network) {
  const { t } = useTranslation()

  const { isNetworkAdmin, isNetworkMember } = useNetworkMemberPermissions({
    networkId: network?._id,
    networkCreatorId: network?.createdBy._id,
    members: network?.members,
  })

  return useMemo(() => {
    if (!network) return []

    return _.compact([
      { id: 'network-home', label: t('page.network.menu.home'), icon: <HomeRoundedIcon />, link: `/networks/${network._id}` },
      isNetworkAdmin && {
        id: 'network-admin',
        label: (
          <Badge
            color="primary"
            invisible={!network.pendingMembers.length}
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                right: 0,
                top: '50%',
                transform: 'translate(150%, -50%)',
              },
            }}
          >
            {t('page.network.menu.adminTool')}
          </Badge>
        ),
        icon: <AdminPanelSettingsRoundedIcon />,
        subItems: _.compact([
          {
            id: 'act-config',
            label: t('page.network.menu.actConfig', { defaultValue: 'Act Configuration' }),
            icon: <SettingsSuggestRoundedIcon />,
            link: `/networks/${network._id}/act-config`,
          },
          {
            id: 'desmos-config',
            label: t('page.network.menu.desmosConfig', { defaultValue: 'Desmos Configuration (legacy)' }),
            icon: <SettingsSuggestRoundedIcon />,
            link: `/networks/${network._id}/desmos-config`,
          },
          {
            id: 'time-tracking',
            label: t('page.network.menu.timeTracking', { defaultValue: 'Time Tracking' }),
            icon: <AccessTimeIcon />,
            link: `/networks/${network._id}/time-tracking`,
          },
          {
            id: 'member-requests',
            label: (
              <Badge
                invisible={!network.pendingMembers.length}
                badgeContent={network.pendingMembers.length}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    right: 0,
                    top: '50%',
                    transform: 'translate(150%, -50%)',
                  },
                }}
              >
                {t('page.network.menu.memberRequests')}
              </Badge>
            ),
            icon: <PersonAddAlt1RoundedIcon />,
            link: `/networks/${network._id}/member-requests`,
          },
        ]),
      },
      isNetworkMember && {
        id: 'network-plannings',
        label: t('page.network.menu.plannings', { defaultValue: 'Plannings' }),
        icon: <CalendarMonthIcon />,
        link: `/networks/${network._id}/plannings`,
      },
      isNetworkMember && {
        id: 'network-waiting-rooms',
        label: t('page.network.menu.waitingRooms'),
        icon: <ChairIcon />,
        link: `/networks/${network._id}/waiting-rooms`,
      },
      isNetworkMember &&
      network.chatRoom && {
        id: 'network-chat',
        label: t('page.network.menu.chat', { defaultValue: 'Chat' }),
        icon: <MessageIcon />,
        link: `/networks/${network._id}/chat?id=${network.chatRoom}`,
      },
      isNetworkAdmin && {
        id: 'network-settings',
        label: t('page.network.menu.settings'),
        icon: <SettingsSuggestRoundedIcon />,
        link: `/networks/${network._id}/edit`,
      },
    ])
  }, [isNetworkAdmin, isNetworkMember, network, t])
}
